import {
  Euler,
  BoxGeometry,
  Group,
  Mesh,
  MeshStandardMaterial,
  Quaternion,
  Vector3,
  CylinderGeometry,
  SphereGeometry,
  TorusGeometry,
} from 'three';
import { BALANCE } from '../config/balance';
import { clamp, smootherstep } from '../utils/math';
import {
  GUN_LOCAL,
  HEAD_LOCAL,
  MUZZLE_LOCAL,
  NECK_BASE_LOCAL,
  NECK_LINKS,
  headRotation,
  neckJoints,
} from './robotRig';

const UP = new Vector3(0, 1, 0);
/** Rest positions of the two body shells, which the strike lunges. */
const BODY_LOCAL = new Vector3(0, 0, 0);
const CHEST_LOCAL = new Vector3(0, 0.06, -0.55);
/** How far off the body's forward a strike may be aimed, radians. */
const MAX_STRIKE_YAW = 1;
/** Strike phases, as fractions of `combat.meleeStrikeDuration`. */
const STRIKE_WIND_END = 0.34;
const STRIKE_SWING_START = 0.28;
const STRIKE_SWING_END = 0.56;
const scratchDirection = new Vector3();
const scratchMid = new Vector3();
const scratchOffset = new Vector3();
const scratchWorld = new Vector3();
const scratchQuaternion = new Quaternion();
const scratchLean = new Quaternion();
const scratchGunEuler = new Euler(0, 0, 0, 'YXZ');
/** The body's local forward, which the lean rolls about. */
const LOCAL_FORWARD = new Vector3(0, 0, -1);
const LOCAL_RIGHT = new Vector3(1, 0, 0);

interface Leg {
  hip: Vector3;
  upper: Mesh;
  lower: Mesh;
  foot: Mesh;
  phaseOffset: number;
  lastFoot: Vector3;
}

/** The head's aim offset in radians, relative to the body. */
export interface HeadOffset {
  yaw: number;
  pitch: number;
  roll: number;
  /** Body-local translation of the head rig, used by the head-tracking parallax. */
  offset?: Vector3;
}

const ZERO_HEAD_OFFSET: HeadOffset = { yaw: 0, pitch: 0, roll: 0 };

/** Wind-up weight: cocks the leg back and lifts it, then releases the swing. */
function windUpWeight(progress: number): number {
  if (progress <= 0 || progress >= STRIKE_SWING_END) return 0;
  if (progress < STRIKE_WIND_END) return smootherstep(progress / STRIKE_WIND_END);
  return 1 - smootherstep((progress - STRIKE_WIND_END) / (STRIKE_SWING_END - STRIKE_WIND_END));
}

/** Swing weight: the fast sweep across the target, then the settle back. */
function swingWeight(progress: number): number {
  if (progress <= STRIKE_SWING_START || progress >= 1) return 0;
  if (progress < STRIKE_SWING_END) {
    return smootherstep((progress - STRIKE_SWING_START) / (STRIKE_SWING_END - STRIKE_SWING_START));
  }
  return 1 - smootherstep((progress - STRIKE_SWING_END) / (1 - STRIKE_SWING_END));
}

/**
 * A procedural quadruped: body, neck, head, mounted weapon and four two
 * segment legs. The gait is driven by speed, so the robot reads as an animal
 * on every surface (see ADR-0005).
 */
export class RobotView {
  readonly group = new Group();

  private readonly chassis = new Group();
  private readonly legs: Leg[] = [];
  private readonly neckRoot = new Group();
  private readonly neckLinks: Mesh[] = [];
  private readonly neckJointMeshes: Mesh[] = [];
  private readonly neckPoints: Vector3[] = Array.from(
    { length: NECK_LINKS + 1 },
    () => new Vector3(),
  );
  private readonly head = new Group();
  private readonly pod = new Group();
  private readonly mount = new Group();
  private readonly gunRoot = new Group();
  private readonly muzzle: Mesh;
  private readonly body: Mesh;
  private readonly chest: Mesh;
  private gaitPhase = 0;
  /** Seconds since the claw strike began; past the duration when idle. */
  private strikeAge = Number.POSITIVE_INFINITY;
  /** Which front leg is doing the clawing (0 = right, 1 = left). */
  private strikeLeg = 0;
  /** Body-local yaw the claw is sweeping through, radians. */
  private strikeYaw = 0;
  /** Swings started since this view was built. */
  private strikeCount = 0;

  /** The head's current orientation, exposed so the aim can be verified. */
  get headQuaternion(): Quaternion {
    return this.head.quaternion;
  }

  /** True while the camera pod shell is drawn. */
  get headShellVisible(): boolean {
    return this.pod.visible;
  }

  /** True while the AR gun is drawn. */
  get gunVisible(): boolean {
    return this.gunRoot.visible;
  }

  /** True while the body, chest and legs are drawn. */
  get chassisVisible(): boolean {
    return this.chassis.visible;
  }

  /** True while the dorsal Z-mount is drawn. */
  get neckVisible(): boolean {
    return this.neckRoot.visible;
  }

  /**
   * Hide the pod shell and the chassis in first person. The eye is mounted in
   * the camera pod, so the housing would sit in front of the camera, and the
   * camera collision pull-in can otherwise put the body around the near plane.
   * The centred AR gun stays as the viewmodel.
   */
  setFirstPerson(firstPerson: boolean): void {
    this.chassis.visible = !firstPerson;
    this.neckRoot.visible = !firstPerson;
    this.pod.visible = !firstPerson;
    this.mount.visible = !firstPerson;
  }

  /**
   * The muzzle's world position.
   *
   * The tracer and the muzzle flash start here rather than at the camera, so
   * they leave the barrel the player can see.
   */
  muzzleWorldPosition(out = new Vector3()): Vector3 {
    this.group.updateMatrixWorld(true);
    return this.muzzle.getWorldPosition(out);
  }

  constructor() {
    const bodyMaterial = new MeshStandardMaterial({
      color: 0x55606e,
      roughness: 0.6,
      metalness: 0.3,
      emissive: 0x1a2330,
      emissiveIntensity: 0.7,
    });
    const limbMaterial = new MeshStandardMaterial({
      color: 0x748497,
      roughness: 0.55,
      metalness: 0.35,
      emissive: 0x18222e,
      emissiveIntensity: 0.55,
    });
    const accentMaterial = new MeshStandardMaterial({
      color: 0x63f0d0,
      roughness: 0.3,
      metalness: 0.1,
      emissive: 0x1c6f60,
      emissiveIntensity: 0.6,
    });

    this.group.add(this.chassis);

    this.body = new Mesh(new BoxGeometry(0.62, 0.42, 1.15), bodyMaterial);
    this.body.position.copy(BODY_LOCAL);
    this.chassis.add(this.body);

    this.chest = new Mesh(new BoxGeometry(0.5, 0.3, 0.35), accentMaterial);
    this.chest.position.copy(CHEST_LOCAL);
    this.chassis.add(this.chest);

    // A few lit strips keep the dark chassis readable in shadow and anchor
    // the dorsal mount visually.
    const dorsalStrip = new Mesh(new BoxGeometry(0.08, 0.02, 0.9), accentMaterial);
    dorsalStrip.position.set(0, 0.215, 0);
    this.chassis.add(dorsalStrip);

    for (const x of [-0.315, 0.315]) {
      const sideStrip = new Mesh(new BoxGeometry(0.02, 0.04, 0.9), accentMaterial);
      sideStrip.position.set(x, 0.05, 0);
      this.chassis.add(sideStrip);
    }

    // The dorsal Z-mount: a riser, a forward boom and a short drop, with
    // visible knuckles at each elbow.
    const neckMaterial = new MeshStandardMaterial({
      color: 0x8b9bad,
      roughness: 0.5,
      metalness: 0.4,
      emissive: 0x1c2734,
      emissiveIntensity: 0.6,
    });
    const jointMaterial = new MeshStandardMaterial({
      color: 0x63f0d0,
      roughness: 0.3,
      metalness: 0.1,
      emissive: 0x1c6f60,
      emissiveIntensity: 0.6,
    });
    const darkMaterial = new MeshStandardMaterial({
      color: 0x2f3946,
      roughness: 0.5,
      metalness: 0.4,
      emissive: 0x0f141b,
      emissiveIntensity: 0.4,
    });
    const lensMaterial = new MeshStandardMaterial({
      color: 0x9ff7e4,
      roughness: 0.15,
      metalness: 0.1,
      emissive: 0x2fd8b8,
      emissiveIntensity: 1.1,
    });

    for (let index = 0; index < NECK_LINKS; index += 1) {
      const link = new Mesh(new BoxGeometry(0.11, 1, 0.11), neckMaterial);
      this.neckRoot.add(link);
      this.neckLinks.push(link);
    }
    for (let index = 0; index <= NECK_LINKS; index += 1) {
      const joint = new Mesh(new SphereGeometry(index === 0 ? 0.1 : 0.085, 10, 8), jointMaterial);
      this.neckRoot.add(joint);
      this.neckJointMeshes.push(joint);
    }
    const basePlate = new Mesh(new BoxGeometry(0.18, 0.04, 0.18), darkMaterial);
    basePlate.position.copy(NECK_BASE_LOCAL);
    this.neckRoot.add(basePlate);
    this.group.add(this.neckRoot);

    // The camera pod is the head: two forward stereo lenses in a housing that
    // sits on top of the gun, like an optic on a rail.
    this.head.position.copy(HEAD_LOCAL);
    const gimbal = new Mesh(new TorusGeometry(0.1, 0.018, 8, 18), accentMaterial);
    gimbal.rotation.x = Math.PI / 2;
    this.mount.add(gimbal);

    const housing = new Mesh(new BoxGeometry(0.24, 0.16, 0.22), limbMaterial);
    housing.position.set(0, 0.12, 0);
    this.pod.add(housing);

    const podTrim = new Mesh(new BoxGeometry(0.26, 0.03, 0.23), accentMaterial);
    podTrim.position.set(0, 0.205, 0);
    this.pod.add(podTrim);

    const facePlate = new Mesh(new BoxGeometry(0.22, 0.12, 0.025), darkMaterial);
    facePlate.position.set(0, 0.12, -0.112);
    this.pod.add(facePlate);

    for (const x of [-0.055, 0.055]) {
      const ring = new Mesh(new CylinderGeometry(0.044, 0.044, 0.014, 16), darkMaterial);
      ring.rotation.x = Math.PI / 2;
      ring.position.set(x, 0.12, -0.128);
      this.pod.add(ring);

      const lens = new Mesh(new CylinderGeometry(0.03, 0.03, 0.022, 16), lensMaterial);
      lens.rotation.x = Math.PI / 2;
      lens.position.set(x, 0.12, -0.139);
      this.pod.add(lens);
    }

    const statusLight = new Mesh(new BoxGeometry(0.04, 0.018, 0.018), lensMaterial);
    statusLight.position.set(0, 0.205, -0.07);
    this.pod.add(statusLight);

    for (const x of [-0.13, 0.13]) {
      const sideSensor = new Mesh(new BoxGeometry(0.03, 0.08, 0.12), darkMaterial);
      sideSensor.position.set(x, 0.12, 0.02);
      this.pod.add(sideSensor);
    }

    // A longer, more prominent AR: the receiver, handguard, exposed barrel and
    // muzzle device run well past the camera pod so the weapon is the station's
    // focal point rather than a stub.
    const receiver = new Mesh(new BoxGeometry(0.12, 0.12, 0.4), limbMaterial);
    receiver.position.copy(GUN_LOCAL);
    this.gunRoot.add(receiver);

    const handguard = new Mesh(new BoxGeometry(0.09, 0.08, 0.42), neckMaterial);
    handguard.position.set(0, GUN_LOCAL.y, GUN_LOCAL.z - 0.42);
    this.gunRoot.add(handguard);

    const rail = new Mesh(new BoxGeometry(0.08, 0.024, 0.78), darkMaterial);
    rail.position.set(0, GUN_LOCAL.y + 0.068, GUN_LOCAL.z - 0.4);
    this.gunRoot.add(rail);

    for (const z of [GUN_LOCAL.z - 0.16, GUN_LOCAL.z - 0.64]) {
      const sight = new Mesh(new BoxGeometry(0.036, 0.05, 0.024), darkMaterial);
      sight.position.set(0, GUN_LOCAL.y + 0.1, z);
      this.gunRoot.add(sight);
      const dot = new Mesh(new BoxGeometry(0.014, 0.014, 0.014), accentMaterial);
      dot.position.set(0, GUN_LOCAL.y + 0.13, z);
      this.gunRoot.add(dot);
    }

    // The exposed barrel sits between the handguard and the muzzle device, so
    // it never pokes through the front of the muzzle.
    const barrelStart = GUN_LOCAL.z - 0.63;
    const barrelEnd = MUZZLE_LOCAL.z - 0.04;
    const barrelLength = Math.abs(barrelEnd - barrelStart);
    const gunBarrel = new Mesh(new CylinderGeometry(0.02, 0.02, barrelLength, 12), limbMaterial);
    gunBarrel.rotation.x = Math.PI / 2;
    gunBarrel.position.set(0, GUN_LOCAL.y, (barrelStart + barrelEnd) / 2);
    this.gunRoot.add(gunBarrel);

    const magazine = new Mesh(new BoxGeometry(0.07, 0.17, 0.1), darkMaterial);
    magazine.position.set(0, GUN_LOCAL.y - 0.11, GUN_LOCAL.z - 0.02);
    magazine.rotation.x = -0.25;
    this.gunRoot.add(magazine);

    const grip = new Mesh(new BoxGeometry(0.05, 0.1, 0.06), darkMaterial);
    grip.position.set(0, GUN_LOCAL.y - 0.1, GUN_LOCAL.z + 0.2);
    grip.rotation.x = 0.35;
    this.gunRoot.add(grip);

    const stock = new Mesh(new BoxGeometry(0.08, 0.08, 0.18), limbMaterial);
    stock.position.set(0, GUN_LOCAL.y, GUN_LOCAL.z + 0.32);
    this.gunRoot.add(stock);

    this.muzzle = new Mesh(new CylinderGeometry(0.03, 0.03, 0.08, 12), darkMaterial);
    this.muzzle.rotation.x = Math.PI / 2;
    this.muzzle.position.copy(MUZZLE_LOCAL);
    this.gunRoot.add(this.muzzle);

    const muzzleRing = new Mesh(new CylinderGeometry(0.034, 0.034, 0.02, 12), accentMaterial);
    muzzleRing.rotation.x = Math.PI / 2;
    muzzleRing.position.copy(MUZZLE_LOCAL).add(new Vector3(0, 0, -0.02));
    this.gunRoot.add(muzzleRing);

    // The mount column is part of the station, not the viewmodel.
    const column = new Mesh(new BoxGeometry(0.06, 0.24, 0.06), neckMaterial);
    column.position.set(0, -0.06, -0.12);
    this.mount.add(column);

    const mountArm = new Mesh(new BoxGeometry(0.06, 0.06, 0.3), neckMaterial);
    mountArm.position.set(0, -0.16, -0.3);
    this.mount.add(mountArm);

    const yoke = new Mesh(new BoxGeometry(0.1, 0.06, 0.1), accentMaterial);
    yoke.position.set(0, -0.16, -0.12);
    this.mount.add(yoke);

    this.head.add(this.pod, this.mount, this.gunRoot);
    this.group.add(this.head);

    const upperGeometry = new CylinderGeometry(0.07, 0.06, 1, 6);
    const lowerGeometry = new CylinderGeometry(0.055, 0.045, 1, 6);
    const footGeometry = new BoxGeometry(0.16, 0.07, 0.22);

    const hips: Array<{ x: number; z: number; phase: number }> = [
      { x: 0.24, z: -0.4, phase: 0 },
      { x: -0.24, z: -0.4, phase: Math.PI },
      { x: 0.24, z: 0.42, phase: Math.PI },
      { x: -0.24, z: 0.42, phase: 0 },
    ];

    for (const hip of hips) {
      const upper = new Mesh(upperGeometry, limbMaterial);
      const lower = new Mesh(lowerGeometry, limbMaterial);
      const foot = new Mesh(footGeometry, accentMaterial);
      this.chassis.add(upper, lower, foot);
      this.legs.push({
        hip: new Vector3(hip.x, -0.1, hip.z),
        upper,
        lower,
        foot,
        phaseOffset: hip.phase,
        lastFoot: new Vector3(),
      });
    }
  }

  /**
   * @param speed body speed in m/s, used to scale the gait
   * @param grounded whether the feet are attached to a surface
   * @param head the head's aim offset, so the robot visibly looks where the
   *   player is aiming while the body keeps walking
   * @param recoil the weapon's recoil kick, applied to the gun
   * @param lean how far the body banks into a turn, radians
   * @param crouch how far the body is lowered into the sneak pose, 0 to 1
   */
  update(
    dt: number,
    position: Vector3,
    quaternion: Quaternion,
    speed: number,
    grounded: boolean,
    head: HeadOffset = ZERO_HEAD_OFFSET,
    recoil = 0,
    lean = 0,
    crouch = 0,
    /** The Matrix dodge: roll out of a lateral sway, bend back from a forward one. */
    dodge: { roll: number; pitch: number } | null = null,
  ): void {
    this.group.position.copy(position);
    this.group.quaternion.copy(quaternion);

    // The body banks into a turn, the way an animal leans.
    if (lean !== 0) {
      scratchLean.setFromAxisAngle(LOCAL_FORWARD, lean);
      this.group.quaternion.multiply(scratchLean);
    }
    // The dodge tilt sits on top of the turn bank, pivoting about the body's
    // own axes, so the swivel reads on the robot while the eye stays on target.
    if (dodge) {
      if (dodge.roll !== 0) {
        scratchLean.setFromAxisAngle(LOCAL_FORWARD, dodge.roll);
        this.group.quaternion.multiply(scratchLean);
      }
      if (dodge.pitch !== 0) {
        scratchLean.setFromAxisAngle(LOCAL_RIGHT, dodge.pitch);
        this.group.quaternion.multiply(scratchLean);
      }
    }

    // The head turns independently of the body: yaw around the body's up,
    // pitch around its lateral axis, roll around its forward axis.
    headRotation(head.yaw, head.pitch, head.roll, this.head.quaternion);
    // The neck stretches a little so the head does not sink into the body.
    this.head.position.copy(HEAD_LOCAL);
    if (head.offset) this.head.position.add(head.offset);
    this.head.position.y += Math.abs(head.pitch) * 0.06;

    // The gun is mounted to the head pivot and kicks with the recoil.
    scratchGunEuler.set(recoil, 0, 0, 'YXZ');
    this.gunRoot.quaternion.setFromEuler(scratchGunEuler);

    // Flex the two Z-mount knuckles towards the aim so the mount reads as
    // articulated, while the head stays on the camera contract.
    neckJoints(head.yaw, head.pitch, this.neckPoints, head.offset);
    for (let index = 0; index < NECK_LINKS; index += 1) {
      this.orientSegment(
        this.neckLinks[index],
        this.neckPoints[index],
        this.neckPoints[index + 1],
        0,
      );
    }
    for (let index = 0; index <= NECK_LINKS; index += 1) {
      this.neckJointMeshes[index].position.copy(this.neckPoints[index]);
    }

    const stride = Math.min(0.28, 0.06 + speed * 0.045);
    const stepHeight = grounded ? 0.12 : 0.02;
    // The body itself is lowered by the same distance, so shortening the legs
    // keeps the feet on the surface while the chassis and head drop.
    const legLength = 0.44 - Math.min(1, Math.max(0, crouch)) * BALANCE.player.sneakCrouchDrop;

    if (grounded && speed > 0.15) {
      this.gaitPhase += dt * (2.2 + speed * 1.6);
    } else {
      // Settle towards a neutral stance.
      this.gaitPhase += dt * 0.6;
    }

    // The claw strike rides on top of the gait, so a swing taken mid-stride
    // composes with the walk instead of fighting it. It is aimed in the body's
    // frame, so the whole sweep turns to face wherever the blow was aimed.
    const strikeProgress = this.strikeAge / BALANCE.combat.meleeStrikeDuration;
    const strikeWind = windUpWeight(strikeProgress);
    const strikeSwing = swingWeight(strikeProgress);
    const strikeCos = Math.cos(-this.strikeYaw);
    const strikeSin = Math.sin(-this.strikeYaw);
    this.strikeAge += dt;

    for (let index = 0; index < this.legs.length; index += 1) {
      const leg = this.legs[index];
      const phase = this.gaitPhase + leg.phaseOffset;
      const swing = Math.sin(phase);
      const lift = Math.max(0, Math.sin(phase)) * stepHeight * Math.min(1, speed / 3);

      let strikeX = 0;
      let strikeY = 0;
      let strikeZ = 0;
      if (index === this.strikeLeg && (strikeWind > 0 || strikeSwing > 0)) {
        // Cock back and up, then reach forward and across the body, so the claw
        // passes through where the blow lands instead of waving beside it.
        const side = index === 0 ? 1 : -1;
        const reach = side * (strikeSwing * 0.16 - strikeWind * 0.06);
        const forward = strikeWind * 0.22 - strikeSwing * 0.55;
        strikeX = reach * strikeCos + forward * strikeSin;
        strikeZ = -reach * strikeSin + forward * strikeCos;
        strikeY = strikeWind * 0.2 + strikeSwing * 0.05;
      } else if (index < 2) {
        // The claw that is not striking braces the body instead of stepping.
        strikeY = -strikeSwing * 0.03;
        strikeZ = strikeSwing * 0.08;
      }

      scratchWorld.copy(leg.hip);
      scratchWorld.x += strikeX;
      scratchWorld.y += -legLength + lift + strikeY;
      scratchWorld.z += swing * stride * 0.5 + strikeZ;
      leg.lastFoot.copy(scratchWorld);

      // Two segment leg: the knee sits half way with a lateral bulge.
      scratchMid.copy(leg.hip).lerp(scratchWorld, 0.5);
      scratchOffset.copy(scratchWorld).sub(leg.hip);
      const length = scratchOffset.length();
      scratchOffset.normalize();
      scratchMid.addScaledVector(scratchOffset, -0.03);
      scratchMid.y += 0.03;

      this.orientSegment(leg.upper, leg.hip, scratchMid, length * 0.5);
      this.orientSegment(leg.lower, scratchMid, scratchWorld, length * 0.5);

      leg.foot.position.copy(scratchWorld);
      leg.foot.quaternion.copy(this.group.quaternion);
    }

    // The body shells dip into the blow, so the claw reads as the whole animal
    // lunging rather than as one leg waving on its own. The head is a sibling of
    // the chassis, so it keeps tracking the aim above the lunge.
    this.body.position.copy(BODY_LOCAL);
    this.body.position.y -= strikeSwing * 0.02;
    this.body.position.z -= strikeSwing * 0.1;
    this.body.rotation.set(-strikeSwing * 0.1, 0, 0);
    this.chest.position.copy(CHEST_LOCAL);
    this.chest.position.z -= strikeSwing * 0.06;
    this.chest.rotation.set(-strikeSwing * 0.1, 0, 0);
  }

  /**
   * Start the claw strike animation, aimed at a body-local direction.
   *
   * The strike is cosmetic — `Melee` has already decided what it hit — but
   * aiming it at the target (or down the aim, on a miss) is what makes a swing
   * read as a swing that missed rather than as a dead key.
   */
  clawStrike(direction: Vector3): void {
    this.strikeAge = 0;
    this.strikeCount += 1;
    this.strikeYaw = clamp(Math.atan2(direction.x, -direction.z), -MAX_STRIKE_YAW, MAX_STRIKE_YAW);
    // The claw on the side the target is on does the work, so the sweep travels
    // towards it rather than away.
    this.strikeLeg = direction.x >= 0 ? 0 : 1;
  }

  /** True while the claw strike animation is playing. */
  get striking(): boolean {
    return this.strikeAge < BALANCE.combat.meleeStrikeDuration;
  }

  /**
   * How many swings have been started.
   *
   * A stalled frame can advance a swing from start to finish in one step, so
   * "did pressing F start a swing" is answered by counting them rather than by
   * catching one in the act.
   */
  get strikes(): number {
    return this.strikeCount;
  }

  /** The striking claw's foot, in body-local space. */
  clawFootLocal(out = new Vector3()): Vector3 {
    return out.copy(this.legs[this.strikeLeg].lastFoot);
  }

  private orientSegment(mesh: Mesh, from: Vector3, to: Vector3, length: number): void {
    scratchDirection.subVectors(to, from);
    const distance = scratchDirection.length();
    if (distance < 1e-5) return;
    scratchDirection.divideScalar(distance);

    scratchMid.copy(from).add(to).multiplyScalar(0.5);
    mesh.position.copy(scratchMid);
    scratchQuaternion.setFromUnitVectors(UP, scratchDirection);
    mesh.quaternion.copy(scratchQuaternion);
    mesh.scale.set(1, Math.max(0.01, distance), 1);
    void length;
  }
}
